package com.swetdot.swetgram.ui.links

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow

enum class ExternalLinkKind { USER, GROUP, JOIN }

data class ExternalLink(val kind: ExternalLinkKind, val value: String) {
    val canonicalUrl: String
        get() = when (kind) {
            ExternalLinkKind.USER -> "https://swetgram.com/u/$value"
            ExternalLinkKind.GROUP -> "https://swetgram.com/g/$value"
            ExternalLinkKind.JOIN -> "https://swetgram.com/join/$value"
        }
}

/** One pending external destination, retained across sign-in and email verification. */
object ExternalLinkInbox {
    private val mutablePending = MutableStateFlow<String?>(null)
    val pending = mutablePending.asStateFlow()

    fun offer(url: String) {
        if (parseExternalLink(url) != null) mutablePending.value = url
    }

    fun consume(url: String) {
        mutablePending.compareAndSet(url, null)
    }
}

/** Entry point called by both Android intents and SwiftUI's `onOpenURL`. */
fun openExternalLink(url: String) {
    ExternalLinkInbox.offer(url)
}

fun parseExternalLink(raw: String): ExternalLink? {
    val valuePattern = "([A-Za-z0-9._~-]+)"
    val https = Regex("^https://swetgram\\.com/(u|g|join)/$valuePattern/?$")
        .matchEntire(raw.trim())
    if (https != null) {
        val kind = when (https.groupValues[1]) {
            "u" -> ExternalLinkKind.USER
            "g" -> ExternalLinkKind.GROUP
            else -> ExternalLinkKind.JOIN
        }
        return ExternalLink(kind, https.groupValues[2]).takeIf { it.isValid() }
    }

    val scheme = Regex("^swetgram://(user|group|join)/$valuePattern/?$")
        .matchEntire(raw.trim()) ?: return null
    val kind = when (scheme.groupValues[1]) {
        "user" -> ExternalLinkKind.USER
        "group" -> ExternalLinkKind.GROUP
        else -> ExternalLinkKind.JOIN
    }
    return ExternalLink(kind, scheme.groupValues[2]).takeIf { it.isValid() }
}

private fun ExternalLink.isValid(): Boolean =
    value.isNotBlank() && (kind != ExternalLinkKind.JOIN || value.length >= 20)
